import { v5 as uuidv5 } from 'uuid';
import type { ScenarioDeltaTranslationInput } from './scenarioDeltaToPositionIntents';
import { departmentDraftMessage, scenarioDeltaToPositionIntents } from './scenarioDeltaToPositionIntents';
import { toIntent, type CommitScenarioResult } from './commitScenarioDelta';
import { OrgChartEditBatchIntentSchema, type OrgChartEditBatchIntent } from '../models/OrgChartEditBatchIntent';
import type { PositionStaffingResult } from '../models/PositionStaffingIntent';

/** Identity contains IDs/revision only, never employee values. The same legacy
 * revision cannot become a fresh random proposal after a reload or lost reply. */
export function legacyAtlasTransferId(folderId: string, diagramId: string, scenarioId: string, revision: string): string {
  return uuidv5(JSON.stringify(['atlas-legacy-transfer-v1', folderId, diagramId, scenarioId, revision]), uuidv5.URL);
}

export function buildLegacyAtlasTransfer(input: ScenarioDeltaTranslationInput & {
  intentId: string; diagramId: string; graphVersion: number; effectiveAt: string;
  legacyChangeCount: number;
}): OrgChartEditBatchIntent {
  const translated = scenarioDeltaToPositionIntents(input);
  const departmentMessage = departmentDraftMessage(translated.unsupported);
  if (departmentMessage) throw new Error(departmentMessage);
  if (input.legacyChangeCount || translated.unsupported.length || !translated.intents.length || translated.intents.length > 32) {
    throw new Error('This legacy draft cannot be transferred completely. No part was saved. Keep the draft and review its unsupported edits.');
  }
  return OrgChartEditBatchIntentSchema.parse({
    kind: 'edit_batch', intentId: input.intentId, mode: 'scenario', ownerDiagramId: input.diagramId,
    expectedGraphVersion: input.graphVersion, effectiveAt: input.effectiveAt,
    // A compatibility transfer is a new explicit draft, never an inferred merge.
    intents: translated.intents.map((core, index) => toIntent(core, {
      intentId: uuidv5(String(index), input.intentId), ownerDiagramId: input.diagramId,
      expectedGraphVersion: input.graphVersion, effectiveAt: input.effectiveAt,
    })),
  });
}

/** One in-session attempt shared by launchers. Freeze before dispatch; retain
 * both the request and its acknowledged result across remount and local errors. */
export function createLegacyAtlasTransfer(input: {
  request: OrgChartEditBatchIntent;
  submit: (request: OrgChartEditBatchIntent) => Promise<PositionStaffingResult>;
  requireCurrentScope: () => void;
}) {
  const frozen = JSON.stringify(input.request);
  let pending: Promise<CommitScenarioResult> | undefined;
  let accepted: CommitScenarioResult | undefined;
  const run = (): Promise<CommitScenarioResult> => {
    input.requireCurrentScope();
    if (accepted) return Promise.resolve(accepted);
    if (pending) return pending;
    pending = (async () => {
      const sent = JSON.parse(frozen) as OrgChartEditBatchIntent;
      const receipt = await input.submit(sent);
      input.requireCurrentScope();
      if (receipt.intentId !== sent.intentId || receipt.outcome !== 'staged'
        || !receipt.changeSetId || !receipt.scenarioId || !Number.isFinite(receipt.graphVersion)
        || receipt.supersededChangeSetId !== null) {
        throw new Error('The draft save could not be confirmed. Retry this same transfer before making another change.');
      }
      accepted = { status: 'staged', appliedCount: sent.intents.length, unsupported: [],
        changeSetId: receipt.changeSetId, scenarioId: receipt.scenarioId };
      return accepted;
    })().finally(() => { pending = undefined; });
    return pending;
  };
  return { run };
}
